import React from "react";
import { View, StyleSheet } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";

export interface DuplicateItemsWarningModalProps {
  type: "billing" | "payroll" | "both";
  billingCount?: number;
  payrollCount?: number;
  onConfirm: () => void;
  onCancel: () => void;
}

export function DuplicateItemsWarningModal({
  type,
  billingCount = 0,
  payrollCount = 0,
  onConfirm,
  onCancel,
}: DuplicateItemsWarningModalProps) {
  const { colors } = useTheme();

  const getTitle = () => {
    if (type === "both") return "Items Already Exist";
    return type === "billing" ? "Billing Items Already Exist" : "Payroll Items Already Exist";
  };

  const getMessage = () => {
    if (type === "both") {
      const parts: string[] = [];
      if (billingCount > 0) parts.push(`${billingCount} billing item${billingCount !== 1 ? "s" : ""}`);
      if (payrollCount > 0) parts.push(`${payrollCount} payroll item${payrollCount !== 1 ? "s" : ""}`);
      return `${parts.join(" and ")} already linked to selected timecard(s). Creating new items may result in duplicates.`;
    }
    const count = type === "billing" ? billingCount : payrollCount;
    return `${count} timecard${count !== 1 ? "s" : ""} already ${count !== 1 ? "have" : "has"} ${type} items. Creating new items may result in duplicates.`;
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.background, borderColor: colors.border }]}>
      {/* Header */}
      <View style={[styles.header, { backgroundColor: colors.amber.background, borderColor: colors.amber.border }]}>
        <MaterialCommunityIcons name="alert-circle" size={24} color={colors.amber.text} />
        <ThemedText type="subtitle" style={{ color: colors.amber.text, marginLeft: 8 }}>
          {getTitle()}
        </ThemedText>
      </View>

      {/* Body */}
      <View style={styles.body}>
        <ThemedText style={styles.message}>{getMessage()}</ThemedText>
      </View>

      {/* Footer */}
      <View style={styles.footer}>
        <Button
          text="Cancel"
          onPress={onCancel}
          style={[styles.cancelButton, { borderColor: colors.border }]}
          textStyle={{ color: colors.text }}
        />
        <Button
          text="Create Anyway"
          icon="plus"
          onPress={onConfirm}
          style={[styles.confirmButton, { backgroundColor: colors.primary }]}
          textStyle={{ color: "white" }}
          iconStyle={{ color: "white" }}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: 420,
    borderRadius: 12,
    borderWidth: 1,
    overflow: "hidden",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    padding: 16,
    borderBottomWidth: 1,
  },
  body: {
    padding: 16,
  },
  message: {
    fontSize: 15,
    lineHeight: 22,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 8,
    padding: 12,
  },
  cancelButton: {
    height: 36,
    paddingHorizontal: 16,
    backgroundColor: "transparent",
    borderWidth: 1,
  },
  confirmButton: {
    height: 36,
    paddingHorizontal: 16,
  },
});
